import Link from "next/link";

type Size = "sm" | "md";

// Three-part wordmark recreated from the live okpromo.co.uk logo:
//   "OK"        ─ small uppercase mark above the start of PROMO
//   "PROMO"     ─ extrabold main mark
//   "Solutions" ─ Caveat script, accent-green, slight rotation, trailing dot
//
// Sora drives OK + PROMO; Caveat drives Solutions. Both are loaded via
// next/font in src/app/layout.tsx as --font-sora / --font-caveat and exposed
// in globals.css as --font-sans / --font-script.
//
// When the source SVG arrives, replace this whole component body with
// a single <Image src="/logos/okpromo-logo.svg" ... /> — every call-site
// (Header / MobileNav / Footer) keeps working unchanged.
export function Logo({
  size = "md",
  href = "/",
}: {
  size?: Size;
  href?: string | null;
}) {
  const dims =
    size === "sm"
      ? {
          ok: "text-[0.8rem]",
          okGap: "mb-0.5",
          promo: "text-[1.4rem]",
          solutions: "text-[1rem] -mt-1 ml-5",
          dot: "size-1",
        }
      : {
          ok: "text-[1rem] sm:text-[1.1rem]",
          okGap: "mb-1",
          promo: "text-[1.75rem] sm:text-[1.95rem]",
          solutions: "text-[1.25rem] sm:text-[1.4rem] -mt-1.5 ml-7 sm:ml-8",
          dot: "size-1.5",
        };

  const inner = (
    <span
      className="inline-flex flex-col font-sans select-none"
      aria-hidden="false"
    >
      <span
        className={`${dims.ok} ${dims.okGap} text-accent leading-none inline-block`}
        style={{
          fontFamily: "var(--font-script)",
          fontWeight: 600,
          transform: "rotate(-2deg)",
          transformOrigin: "left center",
        }}
      >
        OK
      </span>
      <span
        className={`${dims.promo} font-extrabold uppercase text-text leading-[0.85]`}
        style={{ letterSpacing: "-0.04em" }}
      >
        PROMO
      </span>
      <span
        className={`${dims.solutions} text-accent leading-none inline-flex items-end`}
        style={{
          fontFamily: "var(--font-script)",
          fontWeight: 600,
          transform: "rotate(-2deg)",
          transformOrigin: "left center",
        }}
      >
        Solutions
      </span>
    </span>
  );

  if (href === null) return inner;

  return (
    <Link
      href={href}
      aria-label="OK Promo Solutions — home"
      className="inline-block"
    >
      {inner}
    </Link>
  );
}
